// sound5/events.js: everything that happens on a beat. A look-ahead
// sequencer (called from update(), schedules up to `ahead` seconds past the
// audio clock) plays the chord progression, the felt-piano ostinato, the bell
// shimmer, the IGNITE heartbeat, the GRAVITY clock and the brand motif.
// One-shots share a capped voice pool: the oldest note is faded out first.
import { makePan, clamp } from './util.js';
import { PROG, IMPORTANCE, BELLS, brandMotif } from './music.js';

export class Shots {
  constructor(eng, cap) {
    this.eng = eng;
    this.cap = cap;
    this.live = [];
  }
  count(now) {
    this.live = this.live.filter((v) => v.end > now);
    return this.live.length;
  }
  play(buf, bus, t, gain = 1, pan = 0, rate = 1) {
    const c = this.eng.ctx, now = c.currentTime;
    if (!buf || !(gain > 1e-4)) return null;
    if (this.count(now) >= this.cap) this.live.shift().kill(now);
    const s = c.createBufferSource(), g = c.createGain();
    s.buffer = buf;
    s.playbackRate.value = rate;
    g.gain.value = gain;
    s.connect(g);
    if (pan) { const p = makePan(c, clamp(pan, -1, 1)); g.connect(p.node); p.node.connect(bus.in); } else g.connect(bus.in);
    s.start(t);
    const v = {
      end: t + buf.duration / rate,
      kill: (tt) => {
        try {
          const k = Math.max(tt, t);
          g.gain.setTargetAtTime(0, k, 0.012);
          s.stop(k + 0.08);
        } catch (e) { /* already stopped */ }
      },
    };
    this.live.push(v);
    return v;
  }
}

export class Sequencer {
  constructor(eng) {
    this.eng = eng;
    this.name = null; // current progression
    this.want = null;
    this.wantAt = 0;
    this.ci = 0;
    this.nextChord = 0;
    this.si = 0;
    this.nextStep = 0;
    this.nextSpark = 0;
    this.lastBell = 0;
    this.nextBeat = 0;
    this.nextTick = 0;
    this.motifAt = -1; // pending brand motif (audio time) or -1
  }

  // switch progressions after the new one has been asked for 0.35 s in a row
  // (no re-attacks while a visitor wobbles across a chapter boundary)
  setProg(name, now) {
    if (name === this.name) { this.want = null; return; }
    if (name !== this.want) { this.want = name; this.wantAt = now; }
    if (now - this.wantAt < 0.35 && this.name) return;
    this.want = null;
    this.name = name;
    const L = this.eng.L;
    if (!name) { for (const l of L.chords) l.stop(now, 1.5); return; }
    const P = PROG[name];
    this.eng.reg = P.reg;
    this.ci = 0;
    this.chord(now + 0.02);
    this.nextChord = now + 0.02 + P.dur;
    this.si = 0;
    this.nextStep = now + 0.05;
    this.nextSpark = now + P.step * 1.5;
  }

  chord(t) {
    const P = PROG[this.name], notes = P.chords[this.ci % P.chords.length];
    for (const l of this.eng.L.chords) l.chord(notes, t, P.atk, P.rel, P.amp);
  }

  run(now, ahead, T) {
    const eng = this.eng, end = now + ahead;
    // resync after a stall or a suspended context (never schedule in the past)
    const fix = (x) => (x < now - 0.25 ? now + 0.02 : x);
    this.nextChord = fix(this.nextChord);
    this.nextStep = fix(this.nextStep);
    this.nextSpark = fix(this.nextSpark);
    this.nextBeat = fix(this.nextBeat);
    this.nextTick = fix(this.nextTick);
    const P = this.name ? PROG[this.name] : null;
    if (P) {
      while (this.nextChord < end) {
        if (!T.hold || this.ci % P.chords.length !== 0) { this.ci++; this.chord(this.nextChord); }
        this.nextChord += P.dur;
      }
      // felt-piano ostinato: 8ths, thinned by IMPORTANCE
      while (this.nextStep < end) {
        const k = this.si % 8, row = P.motif ? P.motif[this.ci % P.motif.length] : null;
        if (row && row[k] && T.piano > 0.01 && IMPORTANCE[k] >= T.thin - 1e-6) {
          const vel = 0.55 + 0.45 * IMPORTANCE[k];
          eng.shots.play(eng.bank.get('piano', row[k]), eng.B.piano, this.nextStep + (eng.rng() - 0.5) * 0.012, vel, (row[k] - 74) / 30);
        }
        this.si++;
        this.nextStep += P.step;
      }
      // bell shimmer on a slower grid
      const sstep = P.step * (P.motif ? 2 : 1);
      while (this.nextSpark < end) {
        if (T.sparkle > 0.01 && eng.rng() < 0.25 + 0.6 * T.sparkle) this.bell(this.nextSpark + eng.rng() * sstep * 0.4, P, T);
        this.nextSpark += sstep;
      }
    }
    // IGNITE heartbeat: lub-dub, faster as the hold builds
    if (T.heart > 0.004) {
      while (this.nextBeat < end) {
        const per = 60 / T.bpm, th = eng.bank.get('thump');
        eng.shots.play(th, eng.B.heart, this.nextBeat, 1);
        eng.shots.play(th, eng.B.heart, this.nextBeat + Math.min(0.26, per * 0.32), 0.55, 0, 0.94);
        this.nextBeat += per;
      }
    } else this.nextBeat = end;
    // GRAVITY clock: one tick per 1.25 s, slowed and lowered by the redshift
    if (T.tick > 0.004) {
      while (this.nextTick < end) {
        eng.shots.play(eng.bank.get('tick'), eng.B.tick, this.nextTick, 1, 0.25, Math.pow(2, T.bend / 1200));
        this.nextTick += T.tickEvery;
      }
    } else this.nextTick = end;
    // the brand motif (bells, doubled softly an octave below on the piano)
    if (this.motifAt >= 0 && this.motifAt < end) {
      const t0 = Math.max(this.motifAt, now + 0.02), mo = brandMotif(eng.brand), q = 0.34;
      let t = t0;
      mo.notes.forEach((m, i) => {
        const vel = i === mo.notes.length - 1 ? 1 : 0.7 + 0.3 * eng.rng();
        eng.shots.play(eng.bank.get('bell', m), eng.B.motif, t, vel, (i / (mo.notes.length - 1) - 0.5) * 0.6);
        eng.shots.play(eng.bank.get('piano', m - 12), eng.B.motif, t + 0.004, 0.33 * vel);
        t += mo.beats[i] * q;
      });
      this.motifAt = -1;
      this.motifLen = t - t0;
    }
  }

  bell(t, P, T) {
    const eng = this.eng, notes = P.chords[this.ci % P.chords.length];
    const pcs = new Set(notes.map((m) => m % 12).concat(P.colour || []));
    const pool = BELLS.filter((m) => pcs.has(m % 12) && m !== this.lastBell);
    if (!pool.length) return;
    const m = pool[Math.floor(eng.rng() * pool.length)];
    this.lastBell = m;
    const pan = clamp(T.pan + (eng.rng() * 2 - 1) * 0.6, -0.9, 0.9);
    eng.shots.play(eng.bank.get('bell', m), eng.B.bells, t, 0.35 + 0.65 * eng.rng(), pan);
  }
}
